/* eslint-disable no-console */
import { AddCircleOutline, Cancel, Close, Download, FolderOpen, Help, Menu, NewReleases, Save } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, ListItemButton, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Modal from '@mui/material/Modal';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { MenuDrawerProps } from '../../@types/ContextProviderRelated';
import { EModal } from '../../@types/Enums';
import useResponsiveSizes from '../../hooks/useResponsiveSizes';
import PlanModel from '../../models/standard/PlanModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useLoadingContext } from '../../provider/LoadingProvider';
import { useLoginContext } from '../../provider/LoginProvider';
import { useModalContext } from '../../provider/ModalProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { usePlan } from '../../provider/PlanProvider';
import { SceneDataContext } from '../../provider/SceneDataProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { SnackbarType, useSnackbar } from '../../provider/SnackbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import apiService from '../../utils/apiService';
import { deleteButton, immersightBlue, immersightGrey } from '../../utils/colors';
import { APP_VERSION, DRAWER_BOX_HEIGHT_PADDING } from '../../utils/constants';
import { exportToImageFile, getBoundingBox } from '../../utils/exportUtils';
import { LOCAL_STORAGE_KEY, saveWithExpiration } from '../../utils/localStorageUtils';
import { clearPendingSaveAfterLogin, hasPendingSaveAfterLogin, markPendingSaveAfterLogin } from '../../utils/pendingSaveAfterLoginStorage';
import { convertBuildingAttributesToRegion, ERegionCode } from '../../utils/regionUtils';
import CreateNewModal from '../modals/CreateNewModal';
import DownloadModal from '../modals/download/DownloadModal';
import HelpModal from '../modals/HelpModal';
import PredefinedRoomsModal from '../modals/PredefinedRoomsModal';
import SelectRoomSizeModal from '../modals/SelectRoomSizeModal';
import FlagIcon from './FlagIcon';

// --- Configuration ---
const REGIONS = [
  { code: ERegionCode.DE, label: 'DE' },
  { code: ERegionCode.CH, label: 'CH' },
  { code: ERegionCode.GB, label: 'GB' },
  { code: ERegionCode.US, label: 'US' },
  { code: ERegionCode.GLOBAL, label: 'GLOBAL' },
];

const MenuDrawer = ({ isOpen, onClose }: MenuDrawerProps): JSX.Element => {
  const intl = useIntl();
  const theme = useTheme();
  const { isModalShown, setIsModalShown, activeModal, setActiveModal } = useModalContext();
  const { exportPlan, building } = useBuilding();
  const { planModel, setPlanModel } = usePlan();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { sceneRef } = useContext(SceneDataContext);
  const { resetSelection } = useSelection();
  const { unitType } = useUnitContext();
  const { temporarilyHideAllElements } = useMultiSelectToggleContext();
  const { isLoading, setIsLoading } = useLoadingContext();
  const { isAuthReady, isAuthenticated, openLoginModal, refreshAccessToken, token } = useLoginContext();
  const pendingSaveAfterLoginHandledRef = useRef(false);

  // State for Region Change Modal
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [pendingRegion, setPendingRegion] = useState<string | null>(null);
  const { convertAnnotationsToRegion } = useAnnotations();
  const { setBuilding } = useBuilding();
  const { setUnitType } = useUnitContext();

  const effectiveRegion = planModel.region || ERegionCode.DE;

  const handleRegionChange = (event: SelectChangeEvent) => {
    const newRegion = event.target.value as string;
    if (newRegion !== effectiveRegion) {
      setPendingRegion(newRegion);
      setIsRegionModalOpen(true);
    }
  };
  const handleConfirmRegionChange = () => {
    if (pendingRegion) {
      const updatedBuilding = convertBuildingAttributesToRegion(building, pendingRegion);
      setBuilding(updatedBuilding);
      convertAnnotationsToRegion(pendingRegion);
      setPlanModel(
        (previousPlan) =>
          new PlanModel(
            previousPlan.name,
            previousPlan.id,
            previousPlan.kojeId,
            previousPlan.kojeLevel,
            previousPlan.eventId,
            previousPlan.dataObjectId,
            previousPlan.showroomId,
            previousPlan.appVersion,
            updatedBuilding,
            previousPlan.jsonVersion,
            pendingRegion,
          ),
      );
    }
    setIsRegionModalOpen(false);
    setPendingRegion(null);
  };

  const handleCancelRegionChange = () => {
    setIsRegionModalOpen(false);
    setPendingRegion(null);
  };

  const savePlanLogic = useCallback(
    async (format: string, transparent: boolean, activeToken: string) => {
      const imageFile = await exportToImageFile(sceneRef, building, resetSelection, {
        showGeneralInfo: false,
        width: NaN,
        height: NaN,
        pxPerDm: 80,
        fileName: '',
        format,
        transparent,
        unitType: unitType,
        backgroundColor: '#ffffff',
      });

      const planJsonString = exportPlan();
      const planObject = JSON.parse(planJsonString);

      const params = {
        plan: planObject,
      };

      saveWithExpiration(LOCAL_STORAGE_KEY, planJsonString);

      const formData = new FormData();
      formData.append('params', JSON.stringify(params));
      formData.append('file', imageFile);

      // Pass activeToken as the 3rd argument so apiService can put it in the Header
      return await apiService.postFormData('floorplanner/plan', formData, activeToken);
    },
    [building, exportPlan, resetSelection, sceneRef, unitType],
  );

  const resolveSaveToken = useCallback(
    async (tokenOverride?: string): Promise<string | undefined> => {
      if (tokenOverride) {
        return tokenOverride;
      }

      try {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          return refreshedToken;
        }
      } catch (error) {
        console.warn('Could not refresh Keycloak token before saving plan:', error);
      }

      return undefined;
    },
    [refreshAccessToken],
  );

  const executeSavePlan = useCallback(
    async (tokenToUse: string): Promise<void> => {
      setIsLoading(true);
      try {
        let currentFormat = 'jpg';
        let transparencySet = false;
        if (planModel.kojeId !== -1 && planModel.kojeLevel !== -1) {
          currentFormat = 'png';
          transparencySet = true;
        }

        let response;
        if (currentFormat === 'png') {
          response = await temporarilyHideAllElements(() => savePlanLogic(currentFormat, transparencySet, tokenToUse));
        } else {
          response = await savePlanLogic(currentFormat, transparencySet, tokenToUse);
        }

        if (response && response.status && response.status === 'SUCCESS') {
          showSnackbar(intl.formatMessage({ id: 'SavePlan.success' }), SnackbarType.SUCCESS);
        } else {
          showSnackbar(
            `${intl.formatMessage({ id: 'MenuDrawer.maintenanceModeTitle' })}: ${intl.formatMessage({
              id: 'MenuDrawer.maintenanceModeMessage',
            })}`,
            SnackbarType.WARNING,
          );
        }
      } catch (error) {
        console.error('Error saving plan', error);
        showSnackbar(intl.formatMessage({ id: 'SavePlan.genericError' }), SnackbarType.ERROR);
      } finally {
        setIsLoading(false);
      }
    },
    [intl, planModel.kojeId, planModel.kojeLevel, savePlanLogic, setIsLoading, showSnackbar, temporarilyHideAllElements],
  );

  // --- SAVE LOGIC ---
  const handleSavePlan = useCallback(
    async (tokenOverride?: string, shouldPersistPendingSave = true): Promise<void> => {
      const bounds = getBoundingBox(building);
      if (bounds.height === 0 && bounds.width === 0) {
        showSnackbar(intl.formatMessage({ id: 'SavePlan.error' }), SnackbarType.ERROR);
        return;
      }

      const tokenToUse = await resolveSaveToken(tokenOverride);

      if (!tokenToUse) {
        if (shouldPersistPendingSave) {
          markPendingSaveAfterLogin();
          pendingSaveAfterLoginHandledRef.current = false;
        }

        openLoginModal((newToken) => {
          clearPendingSaveAfterLogin();
          if (newToken) {
            void executeSavePlan(newToken);
          }
        });
        return;
      }

      await executeSavePlan(tokenToUse);
    },
    [building, executeSavePlan, intl, openLoginModal, resolveSaveToken, showSnackbar],
  );

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || !token || isLoading || pendingSaveAfterLoginHandledRef.current || !hasPendingSaveAfterLogin()) {
      return;
    }

    pendingSaveAfterLoginHandledRef.current = true;
    clearPendingSaveAfterLogin();
    void handleSavePlan(token, false);
  }, [handleSavePlan, isAuthReady, isAuthenticated, isLoading, token]);

  const handleDownloadClick = () => {
    // check if user is logged in
    if (!isAuthenticated) {
      // if not, open Login Modal, pass a callback to open Download Modal on success
      openLoginModal(() => {
        setActiveModal(EModal.DOWNLOAD);
        setIsModalShown(true);
      });
      return;
    }

    // if already logged in, open directly
    setActiveModal(EModal.DOWNLOAD);
    setIsModalShown(true);
  };

  useEffect(() => {
    if (isOpen) {
      if (activeModal === EModal.NONE) {
        setActiveModal(EModal.CREATE_NEW);
      }
    }
  }, [isOpen]);

  const sizes = useResponsiveSizes();
  const isRoomSizeModal = activeModal === EModal.SELECT_ROOM_SIZE;
  const isRoomShapePickerModal = activeModal === EModal.PREDEFINED_ROOMS;
  const usesCustomRoomModalFrame = isRoomSizeModal || isRoomShapePickerModal;
  const menuItems = [
    {
      label: intl.formatMessage({ id: 'MenuDrawer.new' }),
      icon: <AddCircleOutline />,
      id: 0,
      onClick: () => setIsModalShown(true),
      disabled: false,
    },
    {
      label: intl.formatMessage({ id: 'MenuDrawer.open' }),
      icon: <FolderOpen />,
      id: 1,
      onClick: () => console.log('Not yet implemented!'),
      disabled: true,
    },
    {
      label: intl.formatMessage({ id: 'MenuDrawer.save' }),
      icon: <Save />,
      id: 2,
      onClick: () => handleSavePlan(),
      disabled: isLoading,
    },
    {
      label: intl.formatMessage({ id: 'MenuDrawer.download' }),
      icon: <Download />,
      id: 3,
      onClick: handleDownloadClick,
      disabled: false,
    },
    {
      label: intl.formatMessage({ id: 'MenuDrawer.help' }),
      icon: <Help />,
      id: 4,
      onClick: () => {
        setActiveModal(EModal.HELP);
        setIsModalShown(true);
      },
      disabled: false,
    },
    {
      label: intl.formatMessage({ id: 'MenuDrawer.news' }),
      icon: <NewReleases />,
      id: 5,
      onClick: () => console.log('Not yet implemented!'),
      disabled: true,
    },
  ];

  const handleMenuItemClick = (menuItemId: number): void => {
    const menuItem = menuItems.find((item) => item.id === menuItemId);
    if (!menuItem || menuItem.disabled) return;
    menuItem.onClick();
    onClose();
  };

  const handleModalClose = () => {
    setIsModalShown(false);
    onClose();
    setActiveModal(EModal.NONE);
  };

  const renderRegionContent = (code: string, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FlagIcon code={code} />
      <Typography variant="body1" sx={{ color: immersightGrey, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <SwipeableDrawer anchor="left" open={isOpen} onOpen={() => null} onClose={onClose} PaperProps={{ sx: { minWidth: '20vw' } }}>
      <Box
        component={'div'}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: theme.spacing(DRAWER_BOX_HEIGHT_PADDING),
        }}
      >
        <Box
          component={'div'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: theme.spacing(2),
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 'unset',
              marginRight: theme.spacing(1),
              color: immersightGrey,
              cursor: 'pointer',
            }}
            onClick={handleModalClose}
          >
            <Menu />
          </ListItemIcon>
          <Typography variant="h6" sx={{ ml: 1, my: 2 }}>
            <FormattedMessage id="MenuDrawer.menu" />
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ mr: 1 }}>
          <Close />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ pt: 1 }}>
        {menuItems.map(({ label, icon, id, disabled }) => (
          <ListItemButton
            key={label}
            onClick={() => handleMenuItemClick(id)}
            disabled={disabled}
            sx={{
              opacity: disabled ? 0.5 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              pointerEvents: disabled ? 'none' : 'auto',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 'unset',
                marginRight: theme.spacing(2),
                color: disabled ? 'gray' : immersightGrey,
              }}
            >
              {icon}
            </ListItemIcon>
            <ListItemText primary={label} sx={{ margin: 0 }} />
          </ListItemButton>
        ))}
      </List>

      {/* Modal logic */}
      <Modal
        className="menuDrawerModal"
        open={isModalShown}
        onClose={handleModalClose}
        BackdropProps={{
          sx: usesCustomRoomModalFrame
            ? {
                backdropFilter: 'blur(6px)',
                backgroundColor: 'rgba(0, 0, 0, 0.32)',
              }
            : undefined,
        }}
      >
        <Box
          component={'div'}
          sx={{
            borderRadius: isRoomSizeModal ? '20px' : isRoomShapePickerModal ? '14px' : 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: usesCustomRoomModalFrame ? 0 : 5,
            width: isRoomSizeModal
              ? { xs: 'calc(100vw - 24px)', sm: 'min(900px, calc(100vw - 48px))' }
              : isRoomShapePickerModal
                ? { xs: 'calc(100vw - 24px)', sm: 'min(760px, calc(100vw - 48px))' }
                : activeModal === EModal.HELP
                  ? '440px'
                  : { xs: '90vw', sm: '60vw', md: '50vw', lg: '40vw', xl: '35vw' },
            height: isRoomSizeModal
              ? 'auto'
              : isRoomShapePickerModal
                ? 'auto'
                : activeModal === EModal.HELP
                  ? '300px'
                  : {
                      xs: sizes.isLandscape ? '70vh' : '48vh',
                      sm: sizes.isLandscape ? '65vh' : '48vh',
                      md: sizes.isLandscape ? '55vh' : '48vh',
                      lg: '48vh',
                      xl: '48vh',
                    },
            maxHeight: usesCustomRoomModalFrame ? 'calc(100vh - 48px)' : undefined,
            minWidth: usesCustomRoomModalFrame ? undefined : activeModal === EModal.HELP ? '200px' : '30vw',
            minHeight: usesCustomRoomModalFrame ? undefined : activeModal === EModal.HELP ? undefined : sizes.loginModalHeight,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: usesCustomRoomModalFrame ? 'hidden' : undefined,
            '&:focus': {
              outline: 'none',
            },
            '&:active': {
              outline: 'none',
            },
          }}
        >
          {activeModal === EModal.CREATE_NEW && <CreateNewModal />}
          {activeModal === EModal.PREDEFINED_ROOMS && <PredefinedRoomsModal />}
          {activeModal === EModal.SELECT_ROOM_SIZE && <SelectRoomSizeModal />}
          {activeModal === EModal.DOWNLOAD && <DownloadModal />}
          {activeModal === EModal.HELP && <HelpModal />}
          {!usesCustomRoomModalFrame && (
            <Box component={'div'} sx={{ padding: theme.spacing(1) }}>
              <IconButton
                id="modalCloseBtn"
                onClick={handleModalClose}
                sx={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  padding: '0',
                }}
              >
                <Cancel sx={{ color: 'lightgray', width: '2rem', height: '2rem' }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Modal>

      {/* --- REVISED REGION PICKER --- */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '80px',
          left: 0,
          width: '100%',
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'text.primary',
            fontSize: '1rem',
            fontWeight: 500,
          }}
        >
          <FormattedMessage id="MenuDrawer.regionLabel" />
        </Typography>
        <FormControl variant="standard">
          <Select
            value={effectiveRegion}
            onChange={handleRegionChange}
            disableUnderline
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  marginTop: '5px',
                  boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                },
              },
            }}
            sx={{
              color: immersightGrey,
              fontSize: '1rem',
              minWidth: '80px',
              '& .MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
                paddingLeft: '8px',
                backgroundColor: 'transparent !important',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiSelect-icon': {
                color: immersightGrey,
              },
              '&.Mui-focused': {
                backgroundColor: 'transparent',
              },
            }}
            renderValue={(selected) => {
              const region = REGIONS.find((r) => r.code === selected);
              return renderRegionContent(region?.code || 'DE', region?.label || 'DE');
            }}
          >
            {REGIONS.map((region) => (
              <MenuItem key={region.code} value={region.code}>
                {renderRegionContent(region.code, region.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Region Change Confirmation Dialog */}
      <Dialog open={isRegionModalOpen} onClose={handleCancelRegionChange} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          <FormattedMessage id="RegionModal.title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <FormattedMessage id="RegionModal.description" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCancelRegionChange}
            sx={{
              backgroundColor: deleteButton,
              marginTop: '20px',
            }}
          >
            <FormattedMessage id="General.cancel" />
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmRegionChange}
            autoFocus
            sx={{
              backgroundColor: immersightBlue,
              marginTop: '20px',
            }}
          >
            <FormattedMessage id="General.ok" />
          </Button>
        </DialogActions>
      </Dialog>

      <Typography
        variant="subtitle1"
        sx={{
          ml: 2,
          my: 2,
          padding: theme.spacing(1),
          color: immersightGrey,
          position: 'absolute',
          bottom: 0,
          fontSize: '0.8rem',
        }}
      >
        © {new Date().getFullYear()} immersight GmbH | {<FormattedMessage id={'MenuDrawer.version'} />} : {APP_VERSION}
      </Typography>
    </SwipeableDrawer>
  );
};

export default MenuDrawer;
